import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { TextOneLine } from '@actual-app/components/text-one-line';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';
import type { TransactionEntity } from '@actual-app/core/types/models';

import { Tarjeta, VacioCompacto } from '#components/mobile/inicio/comunes';
import type { PropsWidget } from '#components/mobile/inicio/comunes';
import { estilos } from '#components/mobile/inicio/inicio.estilos';
import { color, radio } from '#components/mobile/ui/tokens';
import { abrirTripulacion } from '#components/pareja/abrirTripulacion';
import { InicialAutor } from '#components/pareja/InicialAutor';
import { autorDe } from '#components/pareja/tripulacionLogica';
import { useTripulacion } from '#components/pareja/useTripulacion';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useCategoriesById } from '#hooks/useCategories';
import { useDateFormat } from '#hooks/useDateFormat';
import { useFormat } from '#hooks/useFormat';
import { useMetadataPref } from '#hooks/useMetadataPref';
import { useNavigate } from '#hooks/useNavigate';
import { usePayeesById } from '#hooks/usePayees';
import { useQuery } from '#hooks/useQuery';
import { useDispatch } from '#redux';

type Apunte = Pick<
  TransactionEntity,
  'id' | 'date' | 'amount' | 'payee' | 'category' | 'autor'
>;

/**
 * Pareja · «A bordo»: los últimos apuntes de la tripulación, cada uno con la
 * inicial de quien lo apuntó (verde tú, azul tu pareja). Solo sale en los
 * presupuestos de dos o más personas.
 */
export function ABordo({ tamano }: PropsWidget) {
  const { t } = useTranslation();
  const format = useFormat();
  const navigate = useNavigate();
  const dateFormat = useDateFormat() || 'MM/dd/yyyy';
  const tripulantes = useTripulacion();
  const { data: payees } = usePayeesById();
  const { data: categorias } = useCategoriesById();
  const cuantos = tamano === 'compacto' ? 2 : tamano === 'grande' ? 6 : 3;
  const dispatch = useDispatch();
  const [cloudFileId] = useMetadataPref('cloudFileId');
  const [budgetName] = useMetadataPref('budgetName');

  const { data: apuntes } = useQuery<Apunte>(
    () =>
      q('transactions')
        .filter({ autor: { $ne: null } })
        .options({ splits: 'none' })
        .orderBy([{ date: 'desc' }, { sort_order: 'desc' }])
        .limit(cuantos)
        .select(['id', 'date', 'amount', 'payee', 'category', 'autor']),
    [cuantos],
  );

  return (
    <Tarjeta
      titulo={t('Aboard')}
      data-testid="inicio-a-bordo"
      relleno={0}
      accion={
        <Button
          variant="bare"
          data-testid="abrir-tripulacion"
          aria-label={t('See the crew')}
          onPress={() => {
            if (cloudFileId) {
              dispatch(abrirTripulacion(cloudFileId, budgetName ?? ''));
            }
          }}
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            minHeight: 32,
            padding: '3px 10px 3px 4px',
            borderRadius: radio.pildora,
            backgroundColor: color.surface2,
            fontSize: 12,
            fontWeight: 800,
            color: color.fg2,
          }}
        >
          <View style={{ flexDirection: 'row' }}>
            {tripulantes.slice(0, 3).map((p, i) => (
              <View key={p.userId} style={{ marginLeft: i > 0 ? -7 : 0 }}>
                <InicialAutor tripulante={p} size={22} />
              </View>
            ))}
          </View>
          <Trans>Crew ›</Trans>
        </Button>
      }
    >
      {!apuntes || apuntes.length === 0 ? (
        <VacioCompacto
          texto={t('When one of you adds an expense, it shows up here.')}
          accion={t('Add')}
          onAccion={() => void navigate('/transactions/new')}
        />
      ) : (
        <View style={{ padding: '4px 14px' }}>
          {apuntes.map((a, i) => {
            const quien = autorDe(tripulantes, a.autor);
            const payee = a.payee ? payees?.[a.payee]?.name : null;
            const categoria = a.category
              ? categorias?.list[a.category]?.name
              : null;
            const detalle = [
              quien ? (quien.esYo ? t('You') : quien.nombre) : null,
              categoria,
              monthUtils.format(a.date, dateFormat),
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <Button
                key={a.id}
                variant="bare"
                aria-label={payee || t('(No payee)')}
                onPress={() => void navigate(`/transactions/${a.id}`)}
                style={{
                  ...estilos.fila,
                  ...(i > 0 ? estilos.filaSeparada : null),
                  ...estilos.filaBoton,
                }}
              >
                {quien ? (
                  <InicialAutor tripulante={quien} size={30} />
                ) : (
                  <View style={{ width: 30 }} />
                )}
                <View style={estilos.filaTexto}>
                  <TextOneLine style={estilos.filaTitulo}>
                    {payee || t('(No payee)')}
                  </TextOneLine>
                  <TextOneLine style={estilos.filaSub}>{detalle}</TextOneLine>
                </View>
                <PrivacyFilter>
                  <Text style={estilos.resumenValor}>
                    {format(a.amount, 'financial')}
                  </Text>
                </PrivacyFilter>
              </Button>
            );
          })}
        </View>
      )}
    </Tarjeta>
  );
}
